import { IPrintingSettings } from "@/core/app/slices/user/user.type"
import { CASE_TYPE } from "@/core/constants/case.constants"
import { ETopLeftViewType } from "@/shared/types"
import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"

export function loadUcloud1_1State() {
  const { VITE_APP_TYPE } = import.meta.env
  if (VITE_APP_TYPE) {
    console.log("🚀 ~ loadUcloud1_1State ~ VITE_APP_TYPE:", VITE_APP_TYPE)
    return VITE_APP_TYPE === "ucloud"
  }

  try {
    const serializedState = localStorage.getItem("ucloud1_1")
    if (!serializedState) return true
    return JSON.parse(serializedState) as boolean
  } catch (e) {
    return true
  }
}

export async function saveUcloud1_1State(state: boolean) {
  try {
    const serializedState = JSON.stringify(state)
    localStorage.setItem("ucloud1_1", serializedState)
  } catch (e) {
    /* do nothing */
  }
}

interface ISelfPlanState {
  isReuploadSelfPlan: boolean
  onConfirm?: () => void
}

type WorkflowStatus =
  | "clinical"
  | "model-check"
  | "refinement-selfplan"
  | "refinement-submitToUAssist"
  | "STL-print"
  | "doingPresetup"

const defaultState = {
  isPreviewModelLoaded: false,
  curCaseMode: "NewCase", // NewCase, UDPlan, UDModi, UDView, WeDesign,
  ucloud1_1: loadUcloud1_1State(), // whether it's ucloud 1.1 else it's ucloud 2.
  curLeftPage: "",
  curRightPage: "",
  workflowStatus: "clinical" as WorkflowStatus,
  workflowMode: "aidesign", // aidesign, chairside, smartrx, retainer, utouch
  showBottomStatusBar: true, // whether to show bottom ms
  bottomStatusMsg: "", // status message
  bottomPrgress: false, // show progress
  updateStaging: false,
  updateSetup: false,
  smartRxShow: false, // status smartRx (new design)
  lockCaseForEdit: "", // "", "Start", "Locked", "LockFailed"
  isCasePreview: true, // case is empty or with only scan / stls
  // For we design only
  showWeStageBar: false,
  showKFEditor: false, // to show key frame editor for weDesign
  curArch: "all", // up, low, all
  currentStep: 0,
  stageCompareLeftViewRefinementIndex: -1, //compare view, left view refinement index, because normally nodes are "R1,2,...", but sometimes it is epmty, so we dont know refinement index
  stageShowType: "", //normal, compare
  stageShowIpr: false, //false, true
  stageShowVelocity: false,
  // for user select the save tx data or not, "save_yes": user select yes, "save_no":user select no, "save_show":just show to user, "save_cancel": cancel to next
  selectTxDataSave: "save_yes",
  //0:show the yes and no, message "Save the current modified tx?"
  //1:show yes、 no and cancel, message "Save the current modified tx?|Do you want to save the changes to the treatment plan?"
  selectTxButton: 0,
  stageShowMode: "", //"": show normal mode, "passive_aligner": show passive aliger mode
  stageCountZeroWarning: false,
  stageDeleteAllPAWarning: false,
  stageTwoButtonWarningMessage: "",
  stageOneButtonWarningMessage: "",
  stageQuickFunc: "",
  currentTreatmentPlan: {
    txName: "",
    index: 1, //2|1
  },
  stageCompareLoadData: false, //false-->true: load
  showTMChart: false,
  caseRAtype: "" as CASE_TYPE | "", // create case choose type: retainer,aligner
  hasUploadSTLs: [false, false], //Whether or not to upload the [upper,lower] stls in the Upload screen
  isNewCaseHasUpload: false, //Scenarios for new cases, whether or not stl is uploaded
  selTooth: 0,
  switchViewEdit: false, // default view only
  openReopenCaseDlg: false,
  switchGum: true,
  planList: [],
  stageDataLen: {
    up: 0,
    low: 0,
  },
  reportData: {
    attachment: {},
    toothlist: [],
    iprData: {},
  },
  uassistRxShow: false,
  isCreateNewRefinement: false,
  isCreateSelfplanRefinement: false,
  //表示当前新建refinement的编号，如果没做过refinement则为0，第一次做之前会改为1，做过一次后则为2或以上
  // 因为历史原因这里的初始值设为0，其实应该为1，因为有很多地方用0值来判断，这里暂且不改。
  refinementNum: 0,
  openHistorySplitView: false,
  // stageObj
  stageObjBySlice: {
    upList: [],
    downList: [],
    up_current: 0,
    down_current: 0,
  },
  // refinement screen control
  widthAll: false,
  // single arch bool
  singleArchBool: false,
  disabledUpper: false,
  disabledLower: false,

  // collision check state
  openCollisionCheck: false,
  // bite-check state
  openBiteCheck: false,
  unlockAndLogout: false,

  //conversion mask
  conversionObj: {
    show: false,
    isFail: false,
  },
  shouldCloseTab: false,
  currentRefinementName: "",
  // order split screen bool
  openOrderSplit: false,
  // order stl print bool
  openStlPrint: false,

  openReviewPrint: false, // whether to open the review STL dialog

  isDoingPresetup: false, // whether is doing presetup
  // print setting caches
  printSettings: {} as IPrintingSettings,

  // for orderstlprint stage
  selectStageInPrint: {
    up: [],
    low: [],
  },
  // for review print stage
  printedStageInPrint: {
    up: [],
    low: [],
  },
  // print label preview
  openLabelPreview: false,
  // print undercut preview
  openUndercutPreview: false,
  //print trim preview
  openTrimPreview: false,
  openStagingIPR: false,
  showJsPreview: false, // whether to show the js preview
  isPrintAsDPA: false, // whether to print as DPA
  openInitialFinal: false,
  cacheAvatar: null, // cache avatar for print
  isCacheAvatar: false, // whether cacheAvatar is set
  // Automated modelcheck Processes after preset-up activeStep
  autoModelCheckStep: 0,
  startAutoModelCheck: false, // whether to go auto model check process
  openSpaceCorrection: false,
  openBiteReg: false,
  openRelabel: false,
  openOrientation: false,
  openGrid: false,
  isOpenModelCheck: false, // is open model check in clinical page
  openSuperimpose: false,
  openLiteMode: false, // whether to open the lite mode
  zoomDisabled: false, // whether to disable zoom in treatment view

  // reupload self plan
  reuploadSelfPlan: {
    isReuploadSelfPlan: false, // whether to open the reuploadSelfPlan mode
    onConfirm: () => {},
  } as ISelfPlanState,

  aligementBtnDisabled: true, // whether to disable the aligement button

  ZoomBarValue: 100, //store current zoom value of treat view

  topLeftViewType: null, // whether to split the view
  isShowExtractedMsg: false,
}

export const udTreatSlice = createSlice({
  name: "udTreatSlice",
  initialState: { ...defaultState },
  reducers: {
    /**
     * Resets the treatment state to its default values.
     *
     * @param state - The current state of the treatment slice.
     *
     * This function clears all the properties in the treatment state and resets them to their initial values as defined in the `defaultState` object.
     */
    setIsPreviewModelLoaded(state, action: PayloadAction<boolean>) {
      state.isPreviewModelLoaded = action.payload
    },
    clearTreatStates(state) {
      return { ...defaultState }
    },
    setCurCaseMode(state, action: PayloadAction<string>) {
      state.curCaseMode = action.payload
    },
    setCurLeftPage(state, action: PayloadAction<string>) {
      state.curLeftPage = action.payload
    },
    setCurRightPage(state, action: PayloadAction<string>) {
      state.curRightPage = action.payload
    },
    setWorkflowMode(state, action: PayloadAction<string>) {
      state.workflowMode = action.payload
    },
    setShowBottomStatusBar(state, action: PayloadAction<boolean>) {
      state.showBottomStatusBar = action.payload
    },
    setBottomStatusMsg(state, action: PayloadAction<string>) {
      state.bottomStatusMsg = action.payload
    },
    setBottomProgress(state, action: PayloadAction<boolean>) {
      state.bottomPrgress = action.payload
    },
    setUpdateSetup(state) {
      state.updateSetup = !state.updateSetup
    },
    setUpdateStaging(state) {
      state.updateStaging = !state.updateStaging
    },
    setSmartRxShow(state, action: PayloadAction<boolean>) {
      state.smartRxShow = action.payload
    },
    setUassistRxShow(state, action: PayloadAction<boolean>) {
      state.uassistRxShow = action.payload
    },
    setLockCaseForEdit(state, action: PayloadAction<string>) {
      state.lockCaseForEdit = action.payload
    },
    setIsCasePreview(state, action: PayloadAction<boolean>) {
      state.isCasePreview = action.payload
    },
    setShowWeStageBar(state, action: PayloadAction<boolean>) {
      state.showWeStageBar = action.payload
    },
    setShowKFEditor(state, action: PayloadAction<boolean>) {
      state.showKFEditor = action.payload
    },
    setCurArch(state, action: PayloadAction<string>) {
      state.curArch = action.payload
    },
    setCurrentStep(state, action: PayloadAction<number>) {
      state.currentStep = action.payload
    },
    setStageCompareLeftViewRefinementIndex(
      state,
      action: PayloadAction<number>,
    ) {
      state.stageCompareLeftViewRefinementIndex = action.payload
    },
    setStageShowType(state, action: PayloadAction<string>) {
      state.stageShowType = action.payload
      if (state.stageShowType === "normal") {
        state.stageCompareLeftViewRefinementIndex = -1
      }
    },
    setStageShowIpr(state, action: PayloadAction<boolean>) {
      state.stageShowIpr = action.payload
    },
    setStageShowVelocity(state, action: PayloadAction<boolean>) {
      state.stageShowVelocity = action.payload
    },
    setSelectTxDataSave(state, action: PayloadAction<string>) {
      state.selectTxDataSave = action.payload
    },
    setSelectTxButton(state, action: PayloadAction<number>) {
      state.selectTxButton = action.payload
    },
    setStageShowMode(state, action: PayloadAction<string>) {
      state.stageShowMode = action.payload
    },
    setStageCountZeroWarning(state, action: PayloadAction<boolean>) {
      state.stageCountZeroWarning = action.payload
    },
    setStageDeleteAllPAWarning(state, action: PayloadAction<boolean>) {
      state.stageDeleteAllPAWarning = action.payload
    },
    setStageTwoButtonWarningMessage(state, action: PayloadAction<string>) {
      state.stageTwoButtonWarningMessage = action.payload
    },
    setStageOneButtonWarningMessage(state, action: PayloadAction<string>) {
      state.stageOneButtonWarningMessage = action.payload
    },
    setStageQuickFunc(state, action: PayloadAction<string>) {
      state.stageQuickFunc = action.payload
    },
    setCurrentTreatmentPlan(
      state,
      action: PayloadAction<{ txName: string; index: 2 | 1 }>,
    ) {
      state.currentTreatmentPlan = action.payload
    },
    setStageCompareLoadData(state, action: PayloadAction<boolean>) {
      state.stageCompareLoadData = action.payload
    },
    setUcloud1_1(state, action: PayloadAction<boolean>) {
      state.ucloud1_1 = action.payload
      saveUcloud1_1State(action.payload)
    },
    setCaseRAtype(state, action: PayloadAction<CASE_TYPE | "">) {
      state.caseRAtype = action.payload
    },
    setHasUploadSTLs(state, action: PayloadAction<boolean[]>) {
      state.hasUploadSTLs = action.payload
    },
    setIsNewCaseHasUpload(state, action: PayloadAction<boolean>) {
      state.isNewCaseHasUpload = action.payload
    },
    setSelTooth(state, action: PayloadAction<number>) {
      state.selTooth = action.payload
    },
    setSwitchViewEdit(state, action: PayloadAction<boolean>) {
      state.switchViewEdit = action.payload
    },
    setOpenReopenCaseDlg(state, action: PayloadAction<boolean>) {
      state.openReopenCaseDlg = action.payload
    },
    setShowJsPreview(state, action: PayloadAction<boolean>) {
      state.showJsPreview = action.payload
    },
    setSwitchGum(state, action: PayloadAction<boolean>) {
      state.switchGum = action.payload
    },
    setPlanList(state, action: PayloadAction<string[]>) {
      const txArr = []
      action.payload.map((i, j) => {
        txArr.push({
          txName: i.trim(),
          index: j + 1,
        })
      })
      state.planList = txArr
    },
    setStageDataLen(state, action: PayloadAction<{ up: number; low: number }>) {
      state.stageDataLen = action.payload
    },
    setReportData(
      state,
      action: PayloadAction<{
        attachment: object
        toothlist: any[]
        iprData: object
      }>,
    ) {
      state.reportData = action.payload
    },
    setIsCreateNewRefinement(state, action: PayloadAction<boolean>) {
      if (action.payload) {
        state.workflowStatus = "refinement-submitToUAssist"
      } else {
        state.workflowStatus = "clinical"
      }
      state.isCreateNewRefinement = action.payload
    },
    setIsCreateSelfplanRefinement(state, action: PayloadAction<boolean>) {
      if (action.payload) {
        state.workflowStatus = "refinement-selfplan"
      } else {
        state.workflowStatus = "clinical"
      }
      state.isCreateSelfplanRefinement = action.payload
    },
    setRefinementNum(state, action: PayloadAction<number>) {
      state.refinementNum = action.payload
    },
    setOpenHistorySplitView(state, action: PayloadAction<boolean>) {
      state.openHistorySplitView = action.payload
    },
    setStageObjFunc(state, action: PayloadAction<boolean>) {
      state.stageObjBySlice = action.payload
    },
    setWidthAll(state, action: PayloadAction<boolean>) {
      state.widthAll = action.payload
    },
    setSingleArchBool(state, action: PayloadAction<boolean>) {
      state.singleArchBool = action.payload
    },
    setdisabledUpper(state, action: PayloadAction<boolean>) {
      state.disabledUpper = action.payload
    },
    setdisabledLower(state, action: PayloadAction<boolean>) {
      state.disabledLower = action.payload
    },
    setOpenCollisionCheck(state, action: PayloadAction<boolean>) {
      state.openCollisionCheck = action.payload
    },
    setOpenBiteCheck(state, action: PayloadAction<boolean>) {
      state.openBiteCheck = action.payload
    },
    setUnlockAndLogout(state, action: PayloadAction<boolean>) {
      state.unlockAndLogout = action.payload
    },
    // conversition obj
    setConversionObj(
      state,
      action: PayloadAction<{ show: boolean; isFail: boolean }>,
    ) {
      state.conversionObj = action.payload
    },
    setShouldCloseTab(state, action: PayloadAction<boolean>) {
      state.shouldCloseTab = action.payload
    },
    setCurrentRefinementName(state, action: PayloadAction<string>) {
      state.currentRefinementName = action.payload
    },
    setOpenOrderSplit(state, action: PayloadAction<boolean>) {
      state.openOrderSplit = action.payload
    },
    setOpenStlPrint(state, action: PayloadAction<boolean>) {
      if (action.payload) {
        state.workflowStatus = "STL-print"
      } else {
        state.workflowStatus = "clinical"
      }
      state.openStlPrint = action.payload
    },
    setOpenReviewPrint(state, action: PayloadAction<boolean>) {
      state.openReviewPrint = action.payload
    },
    setIsDoingPresetup(state, action: PayloadAction<boolean>) {
      if (action.payload) {
        state.workflowStatus = "doingPresetup"
      } else {
        state.workflowStatus = "clinical"
      }
      state.isDoingPresetup = action.payload
    },
    setSelectStageInPrint(
      state,
      action: PayloadAction<{ up: number[]; low: number[] }>,
    ) {
      state.selectStageInPrint = action.payload
    },
    setOpenLabelPreview(state, action: PayloadAction<boolean>) {
      state.openLabelPreview = action.payload
    },
    setOpenUndercutPreview(state, action: PayloadAction<boolean>) {
      state.openUndercutPreview = action.payload
    },
    setOpenTrimPreview(state, action: PayloadAction<boolean>) {
      state.openTrimPreview = action.payload
    },
    setPrintedStageInPrint(
      state,
      action: PayloadAction<{ up: number[]; low: number[] }>,
    ) {
      const { up, low } = action.payload

      const addUnique = (target: number[], values: number[]) => {
        values.forEach((val) => {
          if (!target.includes(val)) {
            target.push(val)
          }
        })
      }

      addUnique(state.printedStageInPrint.up, up)
      addUnique(state.printedStageInPrint.low, low)
    },
    clearPrintedStageInPrint(state) {
      const { up, low } = state.printedStageInPrint
      if (up.length === 0 && low.length === 0) return

      state.printedStageInPrint.up = []
      state.printedStageInPrint.low = []
    },
    setOpenStagingIPR(state, action: PayloadAction<boolean>) {
      state.openStagingIPR = action.payload
    },
    setIsPrintAsDPA(state, action: PayloadAction<boolean>) {
      state.isPrintAsDPA = action.payload
    },
    setOpenInitialFinal(state, action: PayloadAction<boolean>) {
      state.openInitialFinal = action.payload
    },
    setPrintSettings(state, action: PayloadAction<object>) {
      state.printSettings = { ...state.printSettings, ...action.payload }
    },
    clearPrintSettings(state) {
      state.printSettings = {}
    },
    setCacheAvatar(state, action: PayloadAction<any>) {
      state.cacheAvatar = action.payload
    },
    setIsCacheAvatar(state, action: PayloadAction<boolean>) {
      state.isCacheAvatar = action.payload
    },
    setAutoModelCheckStep(state, action: PayloadAction<number>) {
      state.autoModelCheckStep = action.payload
    },
    setStartAutoModelCheck(state, action: PayloadAction<boolean>) {
      if (action.payload) {
        state.workflowStatus = "model-check"
      } else {
        state.workflowStatus = "clinical"
      }

      state.startAutoModelCheck = action.payload
      state.openCollisionCheck = !action.payload
      state.openBiteCheck = !action.payload

      if (!action.payload) {
        state.openOrientation = false
      }
    },
    setOpenSpaceCorrection(state, action: PayloadAction<boolean>) {
      state.openSpaceCorrection = action.payload
      if (action.payload) {
        state.openOrientation = false
      }
    },
    setOpenBiteReg(state, action: PayloadAction<boolean>) {
      state.openBiteReg = action.payload
    },
    setOpenRelabel(state, action: PayloadAction<boolean>) {
      state.openRelabel = action.payload
    },

    setOpenGrid(state, action: PayloadAction<boolean>) {
      if (action.payload) state.openOrientation = !action.payload
      state.openGrid = action.payload
    },
    setopenSuperimpose(state, action: PayloadAction<boolean>) {
      if (action.payload) state.openOrientation = !action.payload
      state.openSuperimpose = action.payload
    },
    setOpenOrientation(state, action: PayloadAction<boolean>) {
      if (action.payload) {
        state.openCollisionCheck = !action.payload
        state.openBiteCheck = !action.payload
        state.openInitialFinal = !action.payload
        state.openSuperimpose = !action.payload
        state.openGrid = !action.payload
      }
      state.openOrientation = action.payload
    },
    setIsOpenModelCheck(state, action: PayloadAction<boolean>) {
      if (action.payload) {
        state.openCollisionCheck = !action.payload
        state.openBiteCheck = !action.payload
        state.openInitialFinal = !action.payload
        state.openSuperimpose = !action.payload
        state.openGrid = !action.payload
        state.curLeftPage = ""
        state.smartRxShow = false
      } else {
        state.openOrientation = false
      }
      state.isOpenModelCheck = action.payload
      state.openLiteMode = action.payload
    },

    setOpenLiteMode(state, action: PayloadAction<boolean>) {
      state.openLiteMode = action.payload
    },
    setZoomDisabled(state, action: PayloadAction<boolean>) {
      state.zoomDisabled = action.payload
    },
    setReuploadSelfPlan(state, action: PayloadAction<ISelfPlanState>) {
      state.reuploadSelfPlan = { ...action.payload }
    },
    setAligementBtnDisabled(state, action: PayloadAction<boolean>) {
      state.aligementBtnDisabled = action.payload
    },
    setZoomBarValue(state, action: PayloadAction<number>) {
      state.ZoomBarValue = action.payload
    },
    setWorkflowStatus(state, action: PayloadAction<WorkflowStatus>) {
      state.workflowStatus = action.payload
    },

    setTopLeftViewType(state, action: PayloadAction<ETopLeftViewType | null>) {
      state.topLeftViewType = action.payload
    },
    setIsShowExtractedMsg(state, action: PayloadAction<boolean>) {
      state.isShowExtractedMsg = action.payload
    },
  },
})

export const {
  setIsPreviewModelLoaded,
  setHasUploadSTLs,
  setCurCaseMode,
  setCurLeftPage,
  setCurRightPage,
  setWorkflowMode,
  setShowBottomStatusBar,
  setBottomStatusMsg,
  setBottomProgress,
  setUpdateSetup,
  setUpdateStaging,
  setSmartRxShow,
  setUassistRxShow,
  setShowWeStageBar,
  setLockCaseForEdit,
  setIsCasePreview,
  setCurArch,
  setShowKFEditor,
  setCurrentStep,
  setStageCompareLeftViewRefinementIndex,
  setStageShowType,
  setStageShowIpr,
  setStageShowVelocity,
  setSelectTxDataSave,
  setSelectTxButton,
  setStageShowMode,
  setStageCountZeroWarning,
  setStageDeleteAllPAWarning,
  setStageTwoButtonWarningMessage,
  setStageOneButtonWarningMessage,
  setStageQuickFunc,
  setCurrentTreatmentPlan,
  setStageCompareLoadData,
  setUcloud1_1,
  setCaseRAtype,
  setIsNewCaseHasUpload,
  setSelTooth,
  setSwitchViewEdit,
  setOpenReopenCaseDlg,
  setSwitchGum,
  setPlanList,
  setStageDataLen,
  setReportData,
  setIsCreateNewRefinement,
  setIsCreateSelfplanRefinement,
  setRefinementNum,
  setOpenHistorySplitView,
  setStageObjFunc,
  setWidthAll,
  setSingleArchBool,
  setdisabledUpper,
  setdisabledLower,
  setOpenCollisionCheck,
  setOpenBiteCheck,
  clearTreatStates,
  setUnlockAndLogout,
  setConversionObj,
  setShouldCloseTab,
  setCurrentRefinementName,
  setOpenOrderSplit,
  setOpenStlPrint,
  setOpenReviewPrint,
  setSelectStageInPrint,
  setOpenLabelPreview,
  setOpenUndercutPreview,
  setOpenTrimPreview,
  setPrintSettings,
  clearPrintSettings,
  setPrintedStageInPrint,
  clearPrintedStageInPrint,
  setOpenStagingIPR,
  setIsPrintAsDPA,
  setOpenInitialFinal,
  setCacheAvatar,
  setIsCacheAvatar,
  setAutoModelCheckStep,
  setStartAutoModelCheck,
  setOpenSpaceCorrection,
  setOpenBiteReg,
  setOpenRelabel,
  setOpenOrientation,
  setIsDoingPresetup,
  setOpenGrid,
  setopenSuperimpose,
  setIsOpenModelCheck,
  setOpenLiteMode,
  setShowJsPreview,
  setZoomDisabled,
  setReuploadSelfPlan,
  setAligementBtnDisabled,
  setZoomBarValue,
  setWorkflowStatus,
  setTopLeftViewType,
  setIsShowExtractedMsg,
} = udTreatSlice.actions
